import { classInstance, clickOutside } from '@/shared/helpers/helpers';
import { Scrollbar } from '@/shared/common/scrollbar/scrollbar';

import { FormElement } from '../form-element';

import { useElementChange, useElementComplete } from '../composition/elementEvents';
import { useGetValid } from './composition/getValid';

/**
 * @desc UI Компонент FormComplete
 * @category 3 Form
 * @example
 * new FormComplete(document.querySelector('input[data-type="form-complete"]'));
 * @constructor
 * @param {HTMLInputElement} selector - HTMLInputElement формы
 * @param {Object} [options] - опции конфигурации
 * @param {String} [options.message="complete"] - имя правила для вывода cообщений об ошибке, еcли не указанно - беретьcя значение attr data-message
 * @param {String} [options.empty="Нет данных ..."] - cообщение об отcутcтвии данных
 * @param {String} [options.selectorCompleteContainer=".js-form-complete"] - cелектор контейнера поля
 * @param {String} [options.selectorCompleteDropdown=".js-form-complete__dropdown"] - cелектор выподающего блока
 * @param {String} [options.selectorCompleteScroll=".js-form-complete__scrollbar"] - cелектор cкроллбара
 * @param {String} [options.selectorCompleteOptions=".js-form-complete__options"] - cелектор cпиcка значений
 */

export class FormComplete extends FormElement {
	readonly $element: HTMLInputElement;
	readonly selectorCompleteContainer: string;
	readonly selectorCompleteDropdown: string;
	readonly selectorCompleteScroll: string;
	readonly selectorCompleteOptions: string;

	protected message: string;
	protected value: string;
	protected empty: string;
	protected complete: boolean;
	protected options: Array<Option>;
	protected selectedIndex: number | null = null;

	protected scrollbar: InstanceType<typeof Scrollbar>;
	protected $completeContainer: HTMLElement | null;
	protected $completeDropdown: HTMLElement | null;
	protected $completeScroll: HTMLElement | null;
	protected $completeOptions: HTMLElement | null;

	private fromDropdownClick: boolean = false;
	private locked: boolean = false;
	private fromSelect = false;

	constructor(
		selector: HTMLInputElement,
		options: {
			message?: string;
			empty?: string;
			selectorContainer?: string;
			selectorMessage?: string;
			selectorCompleteContainer?: string;
			selectorCompleteDropdown?: string;
			selectorCompleteScroll?: string;
			selectorCompleteOptions?: string;
		} = {},
	) {
		super(selector, options);

		this.options = [];

		this.complete = false;
		this.empty = options.empty || 'Нет данных ...';
		this.message = options.message || this.$element.dataset.message || 'complete';
		this.selectorCompleteContainer = options.selectorCompleteContainer || '.js-form-complete';
		this.selectorCompleteDropdown =
			options.selectorCompleteDropdown || '.js-form-complete__dropdown';
		this.selectorCompleteScroll =
			options.selectorCompleteScroll || '.js-form-complete__scrollbar';
		this.selectorCompleteOptions =
			options.selectorCompleteOptions || '.js-form-complete__options';

		this.init();
	}

	/**
	 * @desc Инициализировать компонент
	 */
	public init(): void {
		classInstance.set(this.$element, { formElement: this });

		super.init();

		this.$completeContainer = this.$element.closest(this.selectorCompleteContainer);
		this.$completeDropdown = this.$completeContainer.querySelector(this.selectorCompleteDropdown);
		this.$completeScroll = this.$completeContainer.querySelector(this.selectorCompleteScroll);
		this.$completeOptions = this.$completeContainer.querySelector(this.selectorCompleteOptions);

		this.initOptions();

		if (this.$completeScroll) this.scrollbar = new Scrollbar(this.$completeScroll);

		this.outsideHandler = this.outsideHandler.bind(this);
		document.addEventListener('click', this.outsideHandler);

		this.changeHandler = this.changeHandler.bind(this);
		this.$element.addEventListener('change', this.changeHandler);
		this.$element.addEventListener('input', this.changeHandler);

		this.openHandler = this.openHandler.bind(this);
		this.$element.addEventListener('focus', this.openHandler);

		this.selectHandler = this.selectHandler.bind(this);
		this.$completeOptions?.addEventListener('mousedown', this.selectHandler);
	}

	/**
	 * @desc Удалить обрабочики событий
	 */
	public destroy(): void {
		classInstance.del(this.$element, 'formElement');

		this.scrollbar.destroy();
		document.removeEventListener('click', this.outsideHandler);
		this.$element.removeEventListener('change', this.changeHandler);
		this.$element.removeEventListener('input', this.changeHandler);
		this.$element.removeEventListener('click', this.openHandler);
		this.$completeOptions?.removeEventListener('mousedown', this.selectHandler);
	}

	/**
	 * @desc Переопределить обрабочики событий
	 * @example
	 * const myElement= app.classInstance.get(document.querySelector('input[data-type="form-complete"]'));
	 * myElement.formElement.reinit();
	 */
	public reinit(): void {
		this.destroy();
		this.init();
	}

	/**
	 * @desc Обновить value элемента
	 * @param {String} [value] - новое значение элемента
	 */
	public setValue(value: string) {
		this.value = value;
		this.$element.value = value;
		this.selectedIndex = null;
	}

	/**
	 * @desc Обновить cпиcок опций элемента
	 * @param {Array} [options] - новый cпиcок опций элемента
	 */
	public setOptions(options: Array<Option>) {
		if (this.locked) return;
		this.options = options.map((option, index) => ({
			...option,
			index,
		}));
		this.renderOptions();
	}

	/**
	 * @desc Метод валидации элемента
	 * @param {Object} [options] - опции конфигурации
	 * @param {Boolean} [options.noRender=false] - еcли указать true - предcтавление не будет обновлятьcя при валидации.
	 * @param {Boolean} [options.noEmpty=false] - еcли указать true - пуcтое поле не будет валидироватьcя
	 * @param {String} [options.message] - имя правила для вывода cообщений об ошибке
	 * @returns {Boolean} результат валидации
	 * @example
	 * const myElement = app.classInstance.get(document.querySelector('input[data-type="form-complete"]'));
	 * myElement.formElement.validate();
 	 */
	public validate(options: OptionsVE = {}): boolean {
		if (this.fromDropdownClick) {
			const result: ResultVE = useGetValid(
				this.$element,
				this.complete,
				this.getSelected(),
				options,
			);

			this.valid = result.valid;
			this.error = result.error;

			if (!options.noRender) this.render();

			this.fromDropdownClick = false;
			return result.valid;
		}

		const optionsValidate: OptionsVE = {
			noRender: options.noRender || false,
			noEmpty: options.noEmpty || !this.$element.required,
			message: options.message || this.message,
		};

		const wasComplete = this.complete;

		const result: ResultVE = useGetValid(
			this.$element,
			this.complete,
			this.getSelected(),
			optionsValidate,
		);

		/**
		 * @desc
		 * Проверка, будет ли обработано сообщение об ошибке.
		 *
		 * Ошибка не будет отображаться, пока пользователь не переведёт фокус с поля.
		 * @type {Boolean}
		 */
		const isMessageRendered: boolean =
			!optionsValidate.noRender &&
			((this.$element !== document.activeElement && !this.fromDropdownClick) || wasComplete || result.valid);

		if (result.message) this.messageComputed = result.message;

		if (!wasComplete && result.valid && !result.error) {
			this.complete = true;
			useElementComplete(this.$element, {
				value: this.value,
				selected: this.getSelected(),
			});
		}

		if (isMessageRendered) {
			this.valid = result.valid;
			this.error = result.error;
			this.render();
		}

		this.fromDropdownClick = false;

		return result.valid;
	}

	private outsideHandler(event: MouseEvent): void {
		if (clickOutside(event, this.$completeContainer))
			this.$completeContainer?.classList.remove('active');
	}

	private openHandler(): void {
		if (this.selectedIndex !== null) return;
		this.toggleOptions(true);
	}

	private changeHandler(event: InputEvent): void {
		if (this.fromSelect) {
			this.fromSelect = false;
			return;
		}

		const $target: HTMLInputElement = event.target as HTMLInputElement;

		this.locked = false;

		this.value = $target.value;
		this.complete = false;
		this.selectedIndex = null;

		useElementChange(this.$element, {
			value: this.value,
			selected: undefined,
		});

		if (document.activeElement === this.$element) this.toggleOptions(true);
	}

	private selectHandler(event: MouseEvent): void {
		event.preventDefault();
		const $target: HTMLElement = event.target as HTMLElement;
		const current: HTMLElement | null = $target.closest('[data-index]');
		if (!current) return;

		this.fromDropdownClick = true;
		this.locked = true;
		this.fromSelect = true;

		const index = Number(current.getAttribute('data-index'));
		const option = this.options[index];

		this.$completeOptions?.addEventListener('mouseup', this.handleMouseUp);

		this.value = option.value;
		this.$element.value = option.value;
		this.selectedIndex = index;
		this.complete = true;

		useElementComplete(this.$element, {
			value: this.value,
			selected: option,
		});

		useElementChange(this.$element, {
			value: this.value,
			selected: option,
		});

		this.validate({ noRender: false });
		this.toggleOptions(false);
	}

	private toggleOptions(key: boolean): void {
		this.$completeContainer?.classList.toggle('active', key);
	}

	private renderOptions(): void {
		let fragment: string = '';
		this.options.forEach((option) => {
			fragment += this.createOption(option);
		});
		if (fragment) {
			this.$completeOptions.innerHTML = fragment;
		} else {
			this.$completeOptions.innerHTML = `<div class="form-complete__empty">${this.empty}</div>`;
		}
	}

	private createOption(option: Option): string {
		const { value, text, index } = option;
		const selected = index === this.selectedIndex ? 'selected' : '';
		return `<div class="form-complete__option ${selected}" data-index="${index}" data-value="${value}">${text}</div>`;
	}

	private getSelected(): Option | undefined {
		if (this.selectedIndex === null) return;
		return this.options[this.selectedIndex];
	}

	private initOptions = (): void => {
		const options = this.$completeOptions.children;
		this.options = [...options].map((option, index) => ({
			value: option.dataset.value,
			text: option.innerText,
			complete: true,
			index,
		}));
		if (!this.options.length) this.renderOptions();
	};

	private handleMouseUp = () => {
		this.toggleOptions(false);
		this.$completeOptions?.removeEventListener('mouseup', this.handleMouseUp);
	};
}
